// CP-274: a tab opened before a deploy still runs the old app bundle. Its lazy chunks have content-hashed names
// that the new deploy no longer serves (nginx /js/ and /css/: try_files $uri =404), so the next dynamic import 404s.
// Split recovery (CTO):
//  - Route chunks (lazy views): router.onError reloads ONCE, straight to the target URL, so the fresh no-store
//    index.html brings the new bundle. A sessionStorage stamp guards it: if a reload already happened within
//    RELOAD_WINDOW_MS, show the 'refresh to update' note in the main area instead (no reload loop).
//  - In-page lazy chunks (LineChart, InteractiveMap, BarChart): never reload on their own. They show the note
//    with a Refresh button inside their reserved box; refresh is only ever user-initiated, so nothing typed
//    on the page (e.g. a half-filled Price Alert) is lost.
// Pure: storage, clock and navigation are injected, so node --test can drive it.

export const RELOAD_KEY = 'cs-chunk-reload';
export const RELOAD_WINDOW_MS = 60 * 1000;
export const NOTE_EVENT = 'cs:stale-chunk';

export const NOTE_COPY = {
  // UI/UX (#215 review): neutral wording; true whether the cause is a deploy or a dropped connection.
  section: "This section didn't load. Refresh to get the latest version.",
  page: "This page didn't load. Refresh to get the latest version.",
  button: 'Refresh'
};

const CHUNK_MESSAGE = /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

// webpack 5: ChunkLoadError ("Loading chunk 123 failed."); mini-css-extract: code CSS_CHUNK_LOAD_FAILED
// ("Loading CSS chunk 123 failed."); native ESM (a future Vite build): the browser's dynamic-import messages.
export function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError' || err.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  return CHUNK_MESSAGE.test(String(err.message || err));
}

// True when no reload happened within the window. Unreadable storage (privacy mode, quota) counts as
// "already reloaded", so a broken storage can never cause a loop.
export function canAutoReload(storage, now) {
  try {
    const raw = storage.getItem(RELOAD_KEY);
    if (!raw) return true;
    const at = Number(JSON.parse(raw).at);
    if (!Number.isFinite(at)) return true;
    const age = now - at;
    return age < 0 || age >= RELOAD_WINDOW_MS;
  } catch (_) {
    return false;
  }
}

function stamp(storage, now, url) {
  try {
    storage.setItem(RELOAD_KEY, JSON.stringify({ at: now, url }));
    return true;
  } catch (_) {
    return false;
  }
}

// router.onError. `to` is the route being navigated to (vue-router passes it as the 2nd argument).
// Returns 'reload' (navigated to the target URL), 'note' (guard already fired: showNote(url) called) or
// 'ignore' (not a chunk failure; the caller rethrows).
export function handleRouteChunkError(err, to, { storage, now = Date.now(), assign, showNote }) {
  if (!isChunkLoadError(err)) return 'ignore';
  const url = (to && to.fullPath) || '/';
  if (canAutoReload(storage, now) && stamp(storage, now, url)) {
    assign(url);
    return 'reload';
  }
  if (showNote) showNote(url);
  return 'note';
}

// In-page lazy chunk (chart / map) failed after its own retries: show the note in the box. Never reloads, and
// any failure counts (a blank box helps nobody), chunk 404 or not.
export function handleInPageChunkError(err, { showNote }) {
  showNote(err);
  return 'note';
}
